/**
 * PEStart Wizard Schritt 2 — Investitionen, Kreditplanung & Mitarbeiter-Flags.
 * Team/Gehälter: read-only aus calc.personalData.
 */

import React, { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useAppStore from '../../store/appStore';
import { useCalculationsSelector } from '../../store/selectors/calculations';
import usePEStartStore, {
    ANLAUF_PUFFER_OPTIONS,
    LOAN_LAUFZEIT_OPTIONS,
    getInvestmentSums,
    type InvestmentItem,
    type InvestmentTargetType,
} from '../../store/usePEStartStore';
import { collectStaffFromCalc, computePEStartDashboard } from './peStartCalcAdapter';

const INPUT_CLASS =
    'w-full rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2.5 text-base text-pe-text dark:text-white outline-none focus:border-[#E6C299]/50';

const NUMBER_INPUT_CLASS = `${INPUT_CLASS} appearance-none [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`;

const LABEL_CLASS = 'mb-1.5 block text-xs font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400';

const TARGET_TYPE_OPTIONS: { value: InvestmentTargetType; label: string }[] = [
    { value: 'NEW', label: 'Neugründung' },
    { value: 'TAKEOVER', label: 'Übernahme' },
    { value: 'BOTH', label: 'Beide' },
];

function newId(): string {
    return `id_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 9)}`;
}

function parseNonNegative(raw: string): number {
    const cleaned = String(raw ?? '')
        .replace(/\s/g, '')
        .replace(',', '.');
    if (cleaned === '' || cleaned === '-') return 0;
    const n = Number(cleaned);
    if (!Number.isFinite(n) || n < 0) return 0;
    return n;
}

function parsePositiveInt(raw: string, min: number, max?: number): number {
    const n = parseInt(String(raw), 10);
    if (!Number.isFinite(n)) return min;
    const clamped = Math.max(min, Math.round(n));
    return max !== undefined ? Math.min(max, clamped) : clamped;
}

function numberFieldValue(n: number): string | number {
    if (!Number.isFinite(n) || n === 0) return '';
    return n;
}

function selectInputOnFocus(e: React.FocusEvent<HTMLInputElement>) {
    e.target.select();
}

function itemErrors(item: InvestmentItem): { label?: string; amount?: string; category?: string } {
    const errors: { label?: string; amount?: string; category?: string } = {};
    if (!String(item.label ?? '').trim()) errors.label = 'Bezeichnung erforderlich';
    if (!String(item.category ?? '').trim()) errors.category = 'Kategorie erforderlich';
    if (!(typeof item.amount === 'number') || !Number.isFinite(item.amount) || item.amount < 0) {
        errors.amount = 'Betrag muss ≥ 0 sein';
    }
    return errors;
}

export type PEStartWizardStep2Props = {
    className?: string;
};

const PEStartWizardStep2: React.FC<PEStartWizardStep2Props> = ({ className }) => {
    const calc = useCalculationsSelector() as any;
    const privateCostData = useAppStore((s) => s.clientData?.privateCostData);

    const {
        foundingType,
        investments,
        subsidies,
        staffFlags,
        loanPlan,
        updateInvestments,
        removeInvestmentItem,
        updateLoanPlan,
        setStaffFlag,
        ensureStaffFlags,
    } = usePEStartStore(
        useShallow((s) => ({
            foundingType: s.foundingType,
            investments: s.investments,
            subsidies: s.subsidies,
            staffFlags: s.staffFlags,
            loanPlan: s.loanPlan,
            updateInvestments: s.updateInvestments,
            removeInvestmentItem: s.removeInvestmentItem,
            updateLoanPlan: s.updateLoanPlan,
            setStaffFlag: s.setStaffFlag,
            ensureStaffFlags: s.ensureStaffFlags,
        }))
    );

    const staff = useMemo(() => collectStaffFromCalc(calc), [calc]);
    const currencyCode = calc?.currencyCode || 'EUR';
    const formatCurrency =
        typeof calc?.formatCurrency === 'function'
            ? calc.formatCurrency
            : (v: number, d = 0) =>
                  new Intl.NumberFormat('de-DE', {
                      style: 'currency',
                      currency: currencyCode,
                      maximumFractionDigits: d,
                      minimumFractionDigits: d,
                  }).format(v || 0);

    useEffect(() => {
        ensureStaffFlags(
            staff.map((m) => m.id),
            foundingType === 'TAKEOVER'
        );
    }, [staff, foundingType, ensureStaffFlags]);

    const sums = useMemo(() => getInvestmentSums(investments), [investments]);

    const fb = useMemo(
        () =>
            computePEStartDashboard(
                { foundingType, subsidies, investments, staffFlags, loanPlan },
                calc,
                privateCostData
            ).kpis.finanzbedarf,
        [foundingType, subsidies, investments, staffFlags, loanPlan, calc, privateCostData]
    );

    const visibleItems = useMemo(() => {
        return (investments.items || []).filter((item) => {
            if (item.targetType === 'BOTH') return true;
            return item.targetType === foundingType;
        });
    }, [investments.items, foundingType]);

    const patchItem = (id: string, patch: Partial<InvestmentItem>) => {
        updateInvestments({ items: [{ id, ...patch }] });
    };

    return (
        <section
            className={`min-w-0 overflow-x-hidden rounded-2xl border border-pe-border dark:border-[#2a2a2a] bg-pe-card dark:bg-[#0f0f0f] p-5 text-base ${className ?? ''}`}
        >
            <header className="mb-6 border-b border-pe-border dark:border-[#222] pb-4">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500 dark:text-slate-500">
                    PEStart · Schritt 2
                </p>
                <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299]">
                    Investitionen, Kredit & Team
                </h2>
                <p className="mt-2 text-sm text-stone-500 dark:text-slate-500">
                    Zuerst Kaufpreis/Investitionen erfassen — daraus entsteht der Finanzbedarf.
                    Danach Laufzeit und Zinssatz für die Annuität wählen.
                </p>
            </header>

            {/* Team Referenz + Flags */}
            <div className="mb-6 border-b border-pe-border dark:border-[#222] pb-6">
                <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                    Team aus Programm-Kalkulation
                </p>
                {staff.length === 0 ? (
                    <p className="rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-4 py-3 text-sm text-stone-500 dark:text-slate-500">
                        Keine Mitarbeiter in der Kostenstruktur / Personaldaten. Bitte zuerst Personal
                        erfassen.
                    </p>
                ) : (
                    <ul className="space-y-2">
                        {staff.map((ma) => {
                            const flag = staffFlags[ma.id] || {
                                fromTakeover: foundingType === 'TAKEOVER',
                                startMonth: 1,
                            };
                            return (
                                <li
                                    key={ma.id}
                                    className="flex flex-col gap-3 rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-3 sm:flex-row sm:items-center sm:justify-between"
                                >
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-pe-text dark:text-white">
                                            {ma.name}
                                        </p>
                                        <p className="text-xs text-stone-500 dark:text-slate-500">
                                            {ma.pool} · AG-Kosten{' '}
                                            <span className="text-pe-accent-text dark:text-[#E6C299]">
                                                {formatCurrency(ma.totalCost, 0)}
                                            </span>
                                            {ma.monatsstunden > 0 && (
                                                <> · {ma.monatsstunden.toFixed(0)} Std./Monat</>
                                            )}
                                        </p>
                                        <p className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-600">
                                            Schreibgeschützt aus calc.personalData
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2 text-sm text-pe-muted dark:text-slate-300">
                                            <input
                                                type="checkbox"
                                                checked={!!flag.fromTakeover}
                                                onChange={(e) =>
                                                    setStaffFlag(ma.id, {
                                                        fromTakeover: e.target.checked,
                                                    })
                                                }
                                                className="h-4 w-4 rounded border-pe-border-strong dark:border-[#444] bg-pe-elevated dark:bg-[#1a1a1a] text-pe-accent-text dark:text-[#E6C299]"
                                            />
                                            {flag.fromTakeover ? 'Übernommen' : 'Neuanstellung'}
                                        </label>
                                        <div className="w-28">
                                            <label className={LABEL_CLASS}>Startmonat</label>
                                            <input
                                                type="number"
                                                min={1}
                                                max={6}
                                                step={1}
                                                value={flag.startMonth}
                                                onChange={(e) =>
                                                    setStaffFlag(ma.id, {
                                                        startMonth: parsePositiveInt(
                                                            e.target.value,
                                                            1,
                                                            6
                                                        ),
                                                    })
                                                }
                                                onFocus={selectInputOnFocus}
                                                className={NUMBER_INPUT_CLASS}
                                            />
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* Investitionen */}
            <div className="mb-6 border-b border-pe-border dark:border-[#222] pb-6">
                <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                    Einmal-Investitionen
                </p>

                {foundingType === 'NEW' ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {(
                            [
                                ['erstausstattung', 'Erstausstattung (EUR)'],
                                ['umbau', 'Umbau / Renovierung (EUR)'],
                                ['marketing', 'Marketing / Launch (EUR)'],
                            ] as const
                        ).map(([key, label]) => (
                            <div key={key}>
                                <label className={LABEL_CLASS}>{label}</label>
                                <input
                                    type="number"
                                    min={0}
                                    step={100}
                                    inputMode="decimal"
                                    placeholder="0"
                                    value={numberFieldValue(investments[key] ?? 0)}
                                    onChange={(e) =>
                                        updateInvestments({
                                            [key]: parseNonNegative(e.target.value),
                                        })
                                    }
                                    onFocus={selectInputOnFocus}
                                    className={NUMBER_INPUT_CLASS}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {(
                            [
                                ['kaufpreis', 'Kaufpreis / Ablöse (EUR)'],
                                ['uebernahmekosten', 'Übernahmekosten (EUR)'],
                                ['sanierungInventar', 'Sanierung / Auffrischung (EUR)'],
                            ] as const
                        ).map(([key, label]) => (
                            <div key={key}>
                                <label className={LABEL_CLASS}>{label}</label>
                                <input
                                    type="number"
                                    min={0}
                                    step={100}
                                    inputMode="decimal"
                                    placeholder="0"
                                    value={numberFieldValue(investments[key] ?? 0)}
                                    onChange={(e) =>
                                        updateInvestments({
                                            [key]: parseNonNegative(e.target.value),
                                        })
                                    }
                                    onFocus={selectInputOnFocus}
                                    className={NUMBER_INPUT_CLASS}
                                />
                            </div>
                        ))}
                    </div>
                )}

                <p className="mt-3 rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-4 py-3 text-sm text-pe-muted dark:text-slate-400">
                    Summe {foundingType === 'NEW' ? 'Neugründung' : 'Übernahme'}:{' '}
                    <span className="font-semibold text-pe-accent-text dark:text-[#E6C299]">
                        {formatCurrency(
                            foundingType === 'NEW' ? sums.newTotal : sums.takeoverTotal,
                            0
                        )}
                    </span>
                </p>
            </div>

            {/* Weitere Positionen */}
            <div>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <p className="text-sm font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                            Weitere Positionen (Sonstiges)
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() =>
                            updateInvestments({
                                items: [
                                    {
                                        id: newId(),
                                        category: 'Sonstiges',
                                        label: '',
                                        amount: 0,
                                        targetType: foundingType,
                                        notes: '',
                                    },
                                ],
                            })
                        }
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/15 px-3 py-2.5 text-sm font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/25"
                    >
                        + Position
                    </button>
                </div>

                {visibleItems.length === 0 ? (
                    <p className="rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-4 py-3 text-sm text-stone-500 dark:text-slate-500">
                        Keine zusätzlichen Investitionspositionen.
                    </p>
                ) : (
                    <ul className="space-y-3">
                        {visibleItems.map((row) => {
                            const errs = itemErrors(row);
                            return (
                                <li
                                    key={row.id}
                                    className="space-y-3 rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-3"
                                >
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        <div>
                                            <label className={LABEL_CLASS}>Kategorie</label>
                                            <input
                                                type="text"
                                                value={row.category ?? ''}
                                                onChange={(e) =>
                                                    patchItem(row.id, { category: e.target.value })
                                                }
                                                className={INPUT_CLASS}
                                            />
                                            {errs.category && (
                                                <p className="mt-1 text-xs font-medium text-red-400">
                                                    {errs.category}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Bezeichnung</label>
                                            <input
                                                type="text"
                                                value={row.label ?? ''}
                                                onChange={(e) =>
                                                    patchItem(row.id, { label: e.target.value })
                                                }
                                                className={INPUT_CLASS}
                                            />
                                            {errs.label && (
                                                <p className="mt-1 text-xs font-medium text-red-400">
                                                    {errs.label}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Betrag (EUR)</label>
                                            <input
                                                type="number"
                                                min={0}
                                                step={100}
                                                placeholder="0"
                                                value={numberFieldValue(row.amount)}
                                                onChange={(e) =>
                                                    patchItem(row.id, {
                                                        amount: parseNonNegative(e.target.value),
                                                    })
                                                }
                                                onFocus={selectInputOnFocus}
                                                className={NUMBER_INPUT_CLASS}
                                            />
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Ziel-Typ</label>
                                            <select
                                                value={row.targetType}
                                                onChange={(e) =>
                                                    patchItem(row.id, {
                                                        targetType: e.target
                                                            .value as InvestmentTargetType,
                                                    })
                                                }
                                                className={INPUT_CLASS}
                                            >
                                                {TARGET_TYPE_OPTIONS.map((o) => (
                                                    <option key={o.value} value={o.value}>
                                                        {o.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={() => removeInvestmentItem(row.id)}
                                            className="text-sm font-medium text-red-400/90 underline-offset-2 hover:text-red-300 hover:underline"
                                        >
                                            Entfernen
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* Kreditplanung — erst nach Investitionen */}
            <div className="mb-6 rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4">
                <p className="text-sm font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                    Kreditplanung (Annuität)
                </p>
                <p className="mt-1 text-sm text-stone-500 dark:text-slate-500">
                    Der Kreditbetrag ist der Netto-Finanzbedarf aus deinen Investitionen oben plus
                    Anlauf-Liquiditätspuffer minus Förderungen (Schritt 1). Der Puffer ist bewusst
                    moderat wählbar (Standard 2 Monate) — Förderungen/Reduzierungen kommen separat.
                </p>

                <div className="mt-4">
                    <label className={LABEL_CLASS}>Anlauf-Puffer (Monate)</label>
                    <div className="flex flex-wrap gap-2">
                        {ANLAUF_PUFFER_OPTIONS.map((m) => {
                            const active = (loanPlan?.pufferMonate ?? 2) === m;
                            return (
                                <button
                                    key={m}
                                    type="button"
                                    onClick={() => updateLoanPlan({ pufferMonate: m })}
                                    className={`rounded-lg border px-3 py-2 text-xs font-bold tabular-nums transition-colors ${
                                        active
                                            ? 'border-[#E6C299] bg-[#E6C299]/15 text-pe-accent-text dark:text-[#E6C299]'
                                            : 'border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] text-pe-muted dark:text-slate-400 hover:border-pe-border-strong dark:hover:border-[#444] hover:text-pe-text dark:hover:text-white'
                                    }`}
                                >
                                    {m === 0 ? 'Kein Puffer' : `${m} Mon.`}
                                </button>
                            );
                        })}
                    </div>
                    <p className="mt-1.5 text-xs text-slate-600">
                        = Monate × (Fixkosten + Unternehmerlohn). Bei Übernahme oft 1–2 Monate
                        ausreichend.
                    </p>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <div className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                            + Investitionen / Kaufpreis
                        </p>
                        <p className="mt-1 text-sm font-bold tabular-nums text-pe-text dark:text-white">
                            {formatCurrency(fb.investitionen, 0)}
                        </p>
                    </div>
                    <div className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                            + Anlauf-Puffer ({fb.anlaufPufferMonate} Mon.)
                        </p>
                        <p className="mt-1 text-sm font-bold tabular-nums text-pe-text dark:text-white">
                            {formatCurrency(fb.anlaufLiquiditaetspuffer, 0)}
                        </p>
                        <p className="mt-0.5 text-[10px] text-slate-600">
                            Fixkosten + Unternehmerlohn
                        </p>
                    </div>
                    <div className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                            − Förderungen
                        </p>
                        <p className="mt-1 text-sm font-bold tabular-nums text-pe-accent-text dark:text-[#E6C299]">
                            −{formatCurrency(fb.foerderungen, 0)}
                        </p>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <label className={LABEL_CLASS}>Laufzeit (Jahre)</label>
                        <div className="flex flex-wrap gap-2">
                            {LOAN_LAUFZEIT_OPTIONS.map((y) => {
                                const active = (loanPlan?.laufzeitJahre ?? 10) === y;
                                return (
                                    <button
                                        key={y}
                                        type="button"
                                        onClick={() => updateLoanPlan({ laufzeitJahre: y })}
                                        className={`rounded-lg border px-3 py-2 text-xs font-bold tabular-nums transition-colors ${
                                            active
                                                ? 'border-[#E6C299] bg-[#E6C299]/15 text-pe-accent-text dark:text-[#E6C299]'
                                                : 'border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] text-pe-muted dark:text-slate-400 hover:border-pe-border-strong dark:hover:border-[#444] hover:text-pe-text dark:hover:text-white'
                                        }`}
                                    >
                                        {y} J.
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-2">
                            <input
                                type="number"
                                min={1}
                                max={30}
                                step={1}
                                value={numberFieldValue(loanPlan?.laufzeitJahre ?? 10)}
                                onFocus={selectInputOnFocus}
                                onChange={(e) =>
                                    updateLoanPlan({
                                        laufzeitJahre: parseNonNegative(e.target.value) || 1,
                                    })
                                }
                                className={NUMBER_INPUT_CLASS}
                                placeholder="Eigene Laufzeit 1–30"
                            />
                        </div>
                    </div>
                    <div>
                        <label className={LABEL_CLASS}>Zinssatz p.a. (%)</label>
                        <input
                            type="number"
                            min={0}
                            max={20}
                            step={0.1}
                            value={numberFieldValue(loanPlan?.zinssatzProzent ?? 5.5)}
                            onFocus={selectInputOnFocus}
                            onChange={(e) =>
                                updateLoanPlan({
                                    zinssatzProzent: parseNonNegative(e.target.value),
                                })
                            }
                            className={NUMBER_INPUT_CLASS}
                            placeholder="z. B. 5,5"
                        />
                        <p className="mt-1.5 text-xs text-slate-600">
                            Planungsannahme (Hausbank/KfW-nah). Standard 5,5 % — anpassbar.
                        </p>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                            = Kreditbetrag
                        </p>
                        <p className="mt-1 text-sm font-bold tabular-nums text-pe-text dark:text-white">
                            {formatCurrency(fb.kreditbetrag, 0)}
                        </p>
                        <p className="mt-0.5 text-[10px] text-slate-600">
                            Invest + Puffer − Förderung
                        </p>
                    </div>
                    <div className="rounded-lg border border-[#E6C299]/30 bg-[#E6C299]/10 px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299]/80">
                            Monatsrate
                        </p>
                        <p className="mt-1 text-sm font-black tabular-nums text-pe-accent-text dark:text-[#E6C299]">
                            {formatCurrency(fb.rateMonat, 0)}
                        </p>
                        <p className="mt-0.5 text-[10px] text-slate-600">
                            Annuität · {fb.tilgungLaufzeitJahre} J. · {fb.zinssatzProzent} %
                        </p>
                    </div>
                    <div className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                            davon Zins (Monat 1)
                        </p>
                        <p className="mt-1 text-sm font-bold tabular-nums text-pe-muted dark:text-slate-300">
                            {formatCurrency(fb.zinsAnteilMonat1, 0)}
                        </p>
                        <p className="mt-0.5 text-[10px] text-slate-600">
                            Tilgung M1 {formatCurrency(fb.tilgungAnteilMonat1, 0)}
                        </p>
                    </div>
                    <div className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                            Gesamtzins (Laufzeit)
                        </p>
                        <p className="mt-1 text-sm font-bold tabular-nums text-pe-muted dark:text-slate-300">
                            {formatCurrency(fb.gesamtzins, 0)}
                        </p>
                        <p className="mt-0.5 text-[10px] text-slate-600">
                            Gesamtrückzahlung {formatCurrency(fb.gesamtzahlung, 0)}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default PEStartWizardStep2;
